import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { paymentService } from '@/services';
import { useRegisterStore } from './register'

export const useTerminalStore = defineStore('terminal', () => {
  const connectionStatus = ref(null)
  const loading = ref(false)
  const error = ref(null)
  const lastChecked = ref(null)

  // Terminal management - now register-centric
  const availableTerminals = ref([])
  const selectedTerminal = ref(null)
  const terminalsLoading = ref(false)
  const unassignedTerminals = ref([])
  const unassignedTerminalsLoading = ref(false)
  const availableGateways = ref([])
  const gatewaysLoading = ref(false)
  // Dedupe state for terminals/location fetches
  let loadTerminalsInflight = null
  let terminalsLastFetchedAt = 0

  /**
   * Check terminal connection status for a specific terminal
   * Only call this if the terminal exists and is assigned to a register
   */
  async function checkConnection(terminalId) {
    if (!terminalId) {
      error.value = 'No terminal ID provided'
      return null
    }

    loading.value = true
    error.value = null

    try {
      const response = await paymentService.checkTerminalConnection(terminalId)

      // Extract connection status from nested response structure
      if (response.success && response.connection_status) {
        connectionStatus.value = response.connection_status
      } else {
        connectionStatus.value = response
      }

      lastChecked.value = new Date()
      return response
    } catch (err) {
      error.value = err.message || 'Failed to check terminal connection'
      connectionStatus.value = null
      return null
    } finally {
      loading.value = false
    }
  }

  async function refreshConnection(terminalId) {
    return await checkConnection(terminalId)
  }

  /**
   * Load terminals for a specific location with their register assignments
   * This provides an overview of all terminals in a location
   */
  async function loadTerminalsForLocation(locationId = null) {
    // In-flight + TTL dedupe
    const now = Date.now()
    if (loadTerminalsInflight) return loadTerminalsInflight
    if (now - terminalsLastFetchedAt < 60000 && availableTerminals.value.length > 0) {
      return availableTerminals.value
    }

    terminalsLoading.value = true
    error.value = null

    try {
      loadTerminalsInflight = paymentService.getTerminalsForLocation(locationId)
      const terminals = await loadTerminalsInflight
      availableTerminals.value = terminals
      terminalsLastFetchedAt = Date.now()
      return terminals
    } catch (err) {
      error.value = err.message || 'Failed to load terminals'
      availableTerminals.value = []
      return []
    } finally {
      terminalsLoading.value = false
      loadTerminalsInflight = null
    }
  }

  /**
   * Load active gateway accounts for populating gateway select options
   */
  async function loadActiveGateways() {
    gatewaysLoading.value = true
    error.value = null
    try {
      const resp = await paymentService.getActiveGatewayAccounts()
      availableGateways.value = (resp?.gateways || []).map(g => ({
        id: g.id,
        gateway: g.gateway,
        label: g.label || g.gateway,
        value: g.gateway,
        auth_key_configured: !!g.auth_key_configured
      }))
      return availableGateways.value
    } catch (err) {
      error.value = err.message || 'Failed to load gateways'
      availableGateways.value = []
      return []
    } finally {
      gatewaysLoading.value = false
    }
  }

  /**
   * Create a new terminal
   */
  async function createTerminal({
    label,
    external_id,
    gateway,
    status = 'active',
    credentials = {},
    capabilities = [],
    location_id = null,
    sales_register_id = null,
    merchant_code = null,
    ecom_auth_token = null
  }) {
    loading.value = true
    error.value = null

    try {
      const response = await paymentService.createTerminal({
        label,
        external_id,
        gateway,
        status,
        credentials,
        capabilities,
        location_id,
        sales_register_id,
        merchant_code,
        ecom_auth_token
      })
      return response
    } catch (err) {
      error.value = err.message || 'Failed to create terminal'
      throw err
    } finally {
      loading.value = false
    }
  }

  /**
   * Update an existing terminal
   */
  async function updateTerminal(terminalId, {
    label,
    external_id,
    gateway,
    status = 'active',
    credentials = {},
    capabilities = [],
    location_id = null,
    sales_register_id = null,
    merchant_code = undefined,
    ecom_auth_token = undefined
  }) {
    if (!terminalId) {
      error.value = 'No terminal ID provided'
      return null
    }

    loading.value = true
    error.value = null

    try {
      const updated = await paymentService.updateTerminal(terminalId, {
        label,
        external_id,
        gateway,
        status,
        credentials,
        capabilities,
        location_id,
        sales_register_id,
        merchant_code,
        ecom_auth_token
      })
      // Patch local list if present
      const idx = availableTerminals.value.findIndex(t => t.id === terminalId)
      if (idx !== -1) {
        availableTerminals.value[idx] = { ...availableTerminals.value[idx], ...updated }
      }
      return updated
    } catch (err) {
      error.value = err.message || 'Failed to update terminal'
      throw err
    } finally {
      loading.value = false
    }
  }

  /**
   * Load unassigned terminals (not linked to any register)
   * Useful for terminal management and assignment
   */
  async function loadUnassignedTerminals() {
    unassignedTerminalsLoading.value = true
    error.value = null

    try {
      const terminals = await paymentService.getUnassignedTerminals()
      unassignedTerminals.value = terminals
      return terminals
    } catch (err) {
      error.value = err.message || 'Failed to load unassigned terminals'
      unassignedTerminals.value = []
      return []
    } finally {
      unassignedTerminalsLoading.value = false
    }
  }

  /**
   * Get terminal status for a specific register
   * This is the primary method for checking if a register can process card payments
   */
  async function getTerminalStatusForRegister(registerId) {
    if (!registerId) {
      error.value = 'No register ID provided'
      return null
    }

    loading.value = true
    error.value = null

    try {
      const response = await paymentService.getTerminalStatusForRegister(registerId)
      return response
    } catch (err) {
      error.value = err.message || 'Failed to get terminal status for register'
      return null
    } finally {
      loading.value = false
    }
  }

  /**
   * Get all registers with their terminal assignments for a location
   */
  async function getRegistersWithTerminals(locationId) {
    if (!locationId) {
      error.value = 'No location ID provided'
      return []
    }

    // Delegate to register store to centralize caching/in-flight dedupe
    const registerStore = useRegisterStore()
    try {
      const list = await registerStore.loadRegistersWithTerminals({ force: false })
      return list
    } catch (err) {
      error.value = err.message || 'Failed to get registers with terminals'
      return []
    }
  }

  // Get detailed info for a specific terminal
  async function getTerminalInfo(terminalId) {
    if (!terminalId) return null

    try {
      return await paymentService.getTerminalInfo(terminalId)
    } catch (err) {
      error.value = err.message || 'Failed to get terminal info'
      return null
    }
  }

  // Get transaction summary for a terminal
  async function getTerminalTransactionSummary(terminalId, date = null) {
    if (!terminalId) return null

    try {
      return await paymentService.getTerminalTransactionSummary(terminalId, date)
    } catch (err) {
      error.value = err.message || 'Failed to get terminal summary'
      return null
    }
  }

  // Select a terminal and check its connection
  function selectTerminal(terminalId) {
    selectedTerminal.value = terminalId
    if (terminalId) {
      checkConnection(terminalId)
    }
  }

  // Clear selected terminal
  function clearSelectedTerminal() {
    selectedTerminal.value = null
    connectionStatus.value = null
  }

  // Check if any terminals are available
  const hasTerminals = computed(() => availableTerminals.value.length > 0)

  // Get healthy terminals (ready for payments)
  const healthyTerminals = computed(() =>
    availableTerminals.value.filter(terminal => terminal.is_healthy)
  )

  // Get terminals by gateway type
  const getTerminalsByGateway = computed(() => (gatewayType) =>
    availableTerminals.value.filter(terminal => terminal.gateway === gatewayType)
  )

  // Get terminals assigned to registers
  const assignedTerminals = computed(() =>
    availableTerminals.value.filter(terminal => terminal.register !== null)
  )

  // Get terminals not assigned to registers
  const unassignedTerminalsInLocation = computed(() =>
    availableTerminals.value.filter(terminal => terminal.register === null)
  )

  // Computed properties for connection status
  const isConnected = computed(() => {
    return connectionStatus.value && connectionStatus.value.connected === true
  })

  const connectionColor = computed(() => {
    if (!connectionStatus.value) return 'grey'
    return connectionStatus.value.connected ? 'positive' : 'negative'
  })

  const connectionIcon = computed(() => {
    if (!connectionStatus.value) return 'help'
    return connectionStatus.value.connected ? 'check_circle' : 'error'
  })

  const connectionLabel = computed(() => {
    if (!connectionStatus.value) return 'Unknown'
    return connectionStatus.value.connected ? 'Connected' : 'Disconnected'
  })

  const statusText = computed(() => {
    if (!connectionStatus.value) return 'Terminal status unknown'
    if (connectionStatus.value.connected) {
      const status = connectionStatus.value.raw?.TerminalStatus || connectionStatus.value.status || 'Ready'
      return `Terminal connected - ${status}`
    }
    return `Terminal disconnected - ${connectionStatus.value.error || 'Connection failed'}`
  })

  return {
    // Connection status
    connectionStatus,
    loading,
    error,
    lastChecked,
    checkConnection,
    refreshConnection,
    isConnected,
    connectionColor,
    connectionIcon,
    connectionLabel,
    statusText,

    // Terminal management (register-centric)
    availableTerminals,
    selectedTerminal,
    terminalsLoading,
    unassignedTerminals,
    unassignedTerminalsLoading,
    availableGateways,
    gatewaysLoading,

    // Methods
    loadTerminalsForLocation,
    loadUnassignedTerminals,
    loadActiveGateways,
    getTerminalStatusForRegister,
    getRegistersWithTerminals,
    getTerminalInfo,
    getTerminalTransactionSummary,
    createTerminal,
    updateTerminal,
    selectTerminal,
    clearSelectedTerminal,

    // Computed properties
    hasTerminals,
    healthyTerminals,
    getTerminalsByGateway,
    assignedTerminals,
    unassignedTerminalsInLocation
  }
})
